/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Paging for a track of owner-note slides — the category card's Owners block and the guide
 * row's "From owners" (lifted out of `CardOwners` on 2026-09-17 when the second caller came).
 * The track is native scroll-snap (#118): the browser's scroll-direction lock arbitrates a
 * sideways page-through against a vertical drag, so there is no gesture handler here — only
 * "which slide is showing" read back from the scroll position, and a programmatic `goTo`.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

export interface NoteCarousel {
  trackRef: RefObject<HTMLDivElement>
  active: number
  goTo: (index: number) => void
}

/**
 * `length` re-arms the scroll listener when the slides change. `onPage` runs whenever the
 * showing slide changes, by button or by thumb (the card collapses its expanded note there).
 */
export function useNoteCarousel(length: number, onPage?: () => void): NoteCarousel {
  const trackRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const paged = useRef(onPage)
  paged.current = onPage
  // The slide a button scroll is gliding to: the slides it passes on the way are not "showing".
  const target = useRef<number | null>(null)

  const goTo = useCallback((i: number): void => {
    const el = trackRef.current
    paged.current?.()
    setActive(i)
    target.current = i
    const slide = el?.children[i]
    if (el && slide instanceof HTMLElement) {
      el.scrollTo({ left: slide.offsetLeft - (el.firstElementChild as HTMLElement).offsetLeft })
    }
  }, [])

  // A thumb-driven scroll moves the dots; rAF-coalesced so a fling is one state write per frame.
  useEffect(() => {
    const el = trackRef.current
    if (!el) {
      return undefined
    }
    let frame = 0
    const onScroll = (): void => {
      if (frame) {
        return
      }
      frame = window.requestAnimationFrame(() => {
        frame = 0
        const first = el.firstElementChild as HTMLElement | null
        const second = el.children[1] as HTMLElement | undefined
        const step = first && second ? second.offsetLeft - first.offsetLeft : 0
        if (step > 0) {
          const i = Math.round(el.scrollLeft / step)
          if (target.current !== null) {
            if (i !== target.current) {
              return
            }
            target.current = null
          }
          setActive(prev => {
            if (prev === i) {
              return prev
            }
            paged.current?.()
            return i
          })
        }
      })
    }
    // A swipe that cuts a glide short ends it there; read the slide it settled on.
    const onScrollEnd = (): void => {
      target.current = null
      onScroll()
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    el.addEventListener('scrollend', onScrollEnd)
    return () => {
      el.removeEventListener('scroll', onScroll)
      el.removeEventListener('scrollend', onScrollEnd)
      window.cancelAnimationFrame(frame)
    }
  }, [length])

  return { trackRef, active, goTo }
}
